import { useRef } from 'react';

import useElementSize from '@rio-cloud/rio-uikit/useElementSize';

import { Playground } from '../../../components/playground/Playground';
import { PlaygroundClasses, PlaygroundClassesSpacer } from '../../../components/playground/PlaygroundClasses';
import { Description } from '../../../components/Description';
import { CodeBlock } from '../../../components/CodeBlock';
import Card from '@rio-cloud/rio-uikit/Card';

export const ContainerQueries = () => (
    <div>
        <Description headline='Container queries' />
        <Card className='margin-bottom-25' padding={25}>
            <h3 className='margin-bottom-25 margin-top-0'>What are container queries?</h3>
            <p>
                While traditional container classes like <code>container</code> and <code>fluid-default</code> respond
                to the viewport width,
                <b>container queries</b> respond to the <b>size of the component’s parent container</b>. This allows
                components to adapt their layout based on context, not just screen size.
            </p>
            <p>
                By reacting to the container's width instead of the screen, container queries enable components to
                become context-aware—adapting their layout depending on where they are used. Whether inside a narrow
                sidebar or a wide content area, components can now behave responsively within their own environment,
                making designs more modular, flexible, and reusable.
            </p>
            <table className='table table-head-filled margin-top-20'>
                <thead>
                    <tr>
                        <th>Classic container</th>
                        <th>Container query</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>
                            <code>container</code>, <code>fluid-default</code>
                        </td>
                        <td>
                            e.g., <code>cq-600</code>, <code>cq-700</code> (custom class names)
                        </td>
                    </tr>
                    <tr>
                        <td>Layout adapts to viewport width</td>
                        <td>Layout adapts to container width</td>
                    </tr>
                    <tr>
                        <td>Typically applied at page layout level</td>
                        <td>Used inside components and layout elements</td>
                    </tr>
                    <tr>
                        <td>Based on media queries</td>
                        <td>
                            Based on <code>@container</code> CSS rules
                        </td>
                    </tr>
                </tbody>
            </table>
            <h3 className='margin-y-25'>Usage</h3>
            <p>
                The UIKIT provides atomic utility classes for container queries, enabling you to quickly apply
                conditional styles directly in your JSX/HTML based on container width.
            </p>
            <ol className='space-y-15'>
                <li>
                    <b>Define the container:</b> mark an ancestor element as a container using the specific{' '}
                    <code>cq-container</code> class. This tells the browser which element's size should be monitored.
                    <CodeBlock language='language-html' wrapperClassName='margin-top-10'>
                        {`<div class="cq-container">
    <!-- content goes here -->
</div>`}
                    </CodeBlock>
                </li>
                <li>
                    <b>Apply CQ utility classes:</b> add <code>cq-300:...</code> classes to the descendant elements you
                    want to style conditionally based on the container's.
                    <CodeBlock language='language-html' wrapperClassName='margin-top-10'>
                        {`<div class="cq-container">
    <div class="padding-10 cq-400:padding-15 cq-600:padding-25">
        <!-- more content goes here -->
    </div>
</div>`}
                    </CodeBlock>
                </li>
            </ol>
            <p className='margin-top-20'>
                <b>The class naming convention is:</b> <br />
                <code className='margin-y-10'>cq: + [breakpointValue] + : + [property] + - + [value]</code>
            </p>
            Where:
            <ul>
                <li>cq: is the prefix for all container query utilities.</li>
                <li>
                    [breakpointValue] is one of the numeric identifiers from the table above (e.g., 300, 400, 500, 600,
                    700).
                </li>
                <li>
                    [property] is the type of property being styled (e.g., padding, margin-top, flex-column, grid-cols).
                </li>
                <li>[value] is the value from the library's scale (e.g., 10, 25, auto).</li>
            </ul>
            <h3 className='margin-top-25 padding-top-15 margin-bottom-25'>Container query breakpoints</h3>
            <p>
                Our container query utilities are based on a set of predefined width breakpoints. The breakpoint value
                is included directly in the class name for clarity.
            </p>
            <p>
                We use a "max-width" query for the smallest breakpoint (300px) to target styles up to that size, and
                "min-width" queries for all larger breakpoints to target styles from that size upwards.
            </p>
            <TableBreakpoints />
        </Card>

        <Playground example={containerQueriesExample} classes={containerQueryClasses} />
    </div>
);

const TableBreakpoints = () => {
    const elementRef = useRef<HTMLDivElement>(null);
    const [width] = useElementSize(elementRef);

    return (
        <div ref={elementRef}>
            <table
                className={`table table-head-filled margin-top-20 ${
                    width && width < 400 ? 'table-cards table-single-card' : ''
                }`}
            >
                <thead>
                    <tr>
                        <th>Identifier (in class)</th>
                        <th>Value</th>
                        <th>Query type</th>
                        <th>Description</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td data-field='Identifier'>
                            <span>
                                <code>300</code>
                            </span>
                        </td>
                        <td data-field='Value'>
                            <span>300px</span>
                        </td>
                        <td data-field='Query type'>
                            <span>
                                <code>max-width</code>
                            </span>
                        </td>
                        <td data-field='Description'>
                            <span>Styles applied when container width is ≤ 300px</span>
                        </td>
                    </tr>
                    <tr>
                        <td data-field='Identifier'>
                            <span>
                                <code>400</code>
                            </span>
                        </td>
                        <td data-field='Value'>
                            <span>400px</span>
                        </td>
                        <td data-field='Query type'>
                            <span>
                                <code>min-width</code>
                            </span>
                        </td>
                        <td data-field='Description'>
                            <span>Styles applied when container width is ≥ 400px</span>
                        </td>
                    </tr>
                    <tr>
                        <td data-field='Identifier'>
                            <span>
                                <code>500</code>
                            </span>
                        </td>
                        <td data-field='Value'>
                            <span>500px</span>
                        </td>
                        <td data-field='Query type'>
                            <span>
                                <code>min-width</code>
                            </span>
                        </td>
                        <td data-field='Description'>
                            <span>Styles applied when container width is ≥ 500px</span>
                        </td>
                    </tr>
                    <tr>
                        <td data-field='Identifier'>
                            <span>
                                <code>600</code>
                            </span>
                        </td>
                        <td data-field='Value'>
                            <span>600px</span>
                        </td>
                        <td data-field='Query type'>
                            <span>
                                <code>min-width</code>
                            </span>
                        </td>
                        <td data-field='Description'>
                            <span>Styles applied when container width is ≥ 600px</span>
                        </td>
                    </tr>
                    <tr>
                        <td data-field='Identifier'>
                            <span>
                                <code>700</code>
                            </span>
                        </td>
                        <td data-field='Value'>
                            <span>700px</span>
                        </td>
                        <td data-field='Query type'>
                            <span>
                                <code>min-width</code>
                            </span>
                        </td>
                        <td data-field='Description'>
                            <span>Styles applied when container width is ≥ 700px</span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

const containerQueriesExample = (
    <>
        <h3 className='margin-bottom-25 margin-top-0'>Container query example and classes</h3>
        <div className='cq-container border rounded user-select-none'>
            {/* parent is the container */}
            <div
                className='
                    padding-10 cq-400:padding-15 cq-600:padding-25
                    display-grid grid-cols-1 cq-500:grid-cols-2 cq-600:grid-cols-3
                    gap-5 cq-400:gap-10 cq-500:gap-15 cq-600:gap-20 cq-700:gap-25'
            >
                <div className='padding-y-5 padding-x-10 bg-info display-grid place-items-center rounded'>Item 1</div>
                <div className='padding-y-5 padding-x-10 bg-info display-grid place-items-center rounded'>Item 2</div>
                <div className='padding-y-5 padding-x-10 bg-info display-grid place-items-center rounded'>Item 3</div>
            </div>
        </div>
    </>
);

const containerBreakpoints = [300, 400, 500, 600, 700];

const paddingClasses = ['padding', 'padding-top', 'padding-bottom', 'padding-left', 'padding-right'];
const marginClasses = ['margin', 'margin-top', 'margin-bottom', 'margin-left', 'margin-right'];

const spacingScale = ['0', '5', '10', '15', '20', '25', '50'];
const layoutClasses = ['display-flex', 'flex-row', 'flex-column', 'display-grid', 'col-span-full'];
const gridColCounts = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const textSizes = [10, 11, 12, 14, 16];
const lineHeights = ['125rel', '130rel', '135rel'];

const marginClassList = marginClasses.flatMap(margin =>
    containerBreakpoints.flatMap(breakpoint => spacingScale.map(scale => `${breakpoint}:${margin}-${scale}`))
);

const paddingClassList = paddingClasses.flatMap(padding =>
    containerBreakpoints.flatMap(breakpoint => spacingScale.map(scale => `${breakpoint}:${padding}-${scale}`))
);

const gapClassList = containerBreakpoints.flatMap(breakpoint =>
    spacingScale.map(scale => `${breakpoint}:gap-${scale}`)
);

const rowGapClassList = containerBreakpoints.flatMap(breakpoint =>
    spacingScale.map(scale => `${breakpoint}:row-gap-${scale}`)
);

const columnGapClassList = containerBreakpoints.flatMap(breakpoint =>
    spacingScale.map(scale => `${breakpoint}:column-gap-${scale}`)
);

const layoutClassList = containerBreakpoints.flatMap(breakpoint =>
    layoutClasses.map(layoutClass => `${breakpoint}:${layoutClass}`)
);

const gridColsClassList = gridColCounts.flatMap(col =>
    containerBreakpoints.flatMap(breakpoint => `${breakpoint}:grid-cols-${col}`)
);

const colSpanClassList = gridColCounts.flatMap(col =>
    containerBreakpoints.flatMap(breakpoint => `${breakpoint}:col-span-${col}`)
);

const textSizeClassList = textSizes.flatMap(textSize =>
    containerBreakpoints.flatMap(breakpoint => `${breakpoint}:text-size-${textSize}`)
);

const lineHeightClassList = lineHeights.flatMap(lineHeight =>
    containerBreakpoints.flatMap(breakpoint => `${breakpoint}:line-height-${lineHeight}`)
);

const containerQueryClasses = (
    <>
        <PlaygroundClassesSpacer label='Parent container class' />
        <PlaygroundClasses label='Container' classes='cq-container' />
        <PlaygroundClassesSpacer label='Child classes' />
        <PlaygroundClasses label='Margin' prefix='cq-' classes={marginClassList} />
        <PlaygroundClasses label='Padding' prefix='cq-' classes={paddingClassList} />
        <PlaygroundClasses label='Gap' prefix='cq-' classes={gapClassList} />
        <PlaygroundClasses label='Row/column gap' prefix='cq-' classes={[...rowGapClassList, ...columnGapClassList]} />
        <PlaygroundClasses label='Layout' prefix='cq-' classes={layoutClassList} />
        <PlaygroundClasses label='Grid cols' prefix='cq-' classes={gridColsClassList} />
        <PlaygroundClasses label='Grid col-span' prefix='cq-' classes={colSpanClassList} />
        <PlaygroundClasses label='Text size' prefix='cq-' classes={textSizeClassList} />
        <PlaygroundClasses label='Line height' prefix='cq-' classes={lineHeightClassList} />
    </>
);
